import { createContext, Fragment, isValidElement, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames/bind';
import { Defaults } from 'app/lib/constants';
import { Hint } from 'app/components/core/Hint';
import { Icon, IconSize } from 'app/components/core/Icon';
import { Fieldset } from 'app/components/core/Fieldset';
import { FieldLayout } from 'app/components/core/FieldLayout';
import { Loading, LoadingSize } from 'app/components/core/Loading';
import * as styles from './DialogChapter.css';

const cx = classnames.bind(styles);

const DialogChapterContext = createContext(null);
const useDialogChapterContext = () => useContext(DialogChapterContext);

export function DialogChapterLayout({ id, children }) {
    return (
        <DialogChapterContext.Provider value={id}>
            <FieldLayout>{children}</FieldLayout>
        </DialogChapterContext.Provider>
    );
}

export function CustomSummary({ as: Element = 'div', children, className }) {
    return <Element className={cx('summary', className)}>{children}</Element>;
}

export function KeyValueSummary({ values }) {
    const { t } = useTranslation();

    return (
        <CustomSummary as="dl">
            {Object.entries(values).map(([label, value]) => (
                <Fragment key={label}>
                    <dt>{label}</dt>
                    <dd>{value ?? t('common:naDashes')}</dd>
                </Fragment>
            ))}
        </CustomSummary>
    );
}

export function ListSummary({ values }) {
    return (
        <CustomSummary as="ul">
            {values.map((item) => (
                <li key={item}>{item}</li>
            ))}
        </CustomSummary>
    );
}

export function StringSummary({ value }) {
    return <CustomSummary as="p">{value}</CustomSummary>;
}

export function SummaryHint({ children }) {
    return <Hint className={styles.summary}>{children}</Hint>;
}

export function DialogChapter({
    label,
    direction,
    defaultOpen,
    isProcessing = false,
    isOptional = false,
    summary,
    disabled = false,
    children,
    className,
    onOpen = Defaults.NOOP,
    onClose = Defaults.NOOP
}) {
    const name = useDialogChapterContext();
    const Element = disabled ? 'div' : 'details';

    function handleToggle(event) {
        // Make sure we only handle toggle events on the details element. Toggle
        // events from children (like an error tooltip appearing), can bubble up
        // and trigger this as well.
        if (event.target !== event.currentTarget) return;

        if (event.target.open) {
            onOpen();
        } else {
            onClose();
        }
    }

    return (
        <Element
            className={cx('base', { disabled }, className)}
            name={name}
            onToggle={handleToggle}
            open={defaultOpen}>
            <summary>
                <h4 className={styles.title}>
                    <Icon
                        className={styles.chevron}
                        size={IconSize.EXTRA_SMALL}
                        variant="chevron-right-big"
                    />
                    <span>{label}</span>
                    {isProcessing && <Loading size={LoadingSize.EXTRA_SMALL} />}
                    {isOptional && <span className={styles.optional}>Optional</span>}
                </h4>
                {isValidElement(summary) && summary}
            </summary>
            {!disabled && <Fieldset direction={direction}>{children}</Fieldset>}
        </Element>
    );
}
